// ============================================================
// SHARED TEXT / FORMATTING HELPERS
// ============================================================
// Used by both report builders so numbers, labels, and characters are
// consistent and — critically — safe for jsPDF's default fonts, which
// only reliably render the WinAnsi character set. Arrows (▲▼→), smart
// quotes, em-dashes, and emoji render as broken boxes or vanish entirely
// if used directly, so everything user-facing routes through here.

// "SalesRepresentative" -> "Sales Representative", "order_date" -> "Order Date"
export function humanize(column = "") {
  return String(column)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// Strips/replaces characters outside the safe WinAnsi range that jsPDF's
// built-in fonts (Helvetica/Times/Courier) do not reliably render.
export function safeText(str = "") {
  return String(str)
    .replace(/[\u2018\u2019]/g, "'")      // smart single quotes
    .replace(/[\u201C\u201D]/g, '"')      // smart double quotes
    .replace(/[\u2013\u2014]/g, "-")      // en/em dash
    .replace(/\u2026/g, "...")            // ellipsis
    .replace(/[\u25B2\u25BC\u2192\u2191\u2193]/g, "") // arrows/triangles
    .replace(/[^\x00-\x7E]/g, "");        // anything else non-ASCII (emoji, etc.)
}

// Directional change as SAFE plain text, per spec: "+5.2%" / "-1.6%",
// never a unicode arrow glyph.
export function fmtPercent(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";
  const sign = n > 0 ? "+" : n < 0 ? "" : "";
  return `${sign}${n.toFixed(1)}%`;
}

export function directionWord(direction) {
  if (direction === "increased") return "Increased";
  if (direction === "decreased") return "Decreased";
  if (direction === "stable") return "Stable";
  return "N/A";
}

export function fmtNumber(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 });
}

// Compact business-report style: 19200000 -> "19.2M", 49200 -> "49.2K"
export function fmtCompact(n) {
  if (n === null || n === undefined || isNaN(n)) return "N/A";

  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";

  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1)}K`;

  return `${sign}${abs.toFixed(abs < 10 ? 2 : 0)}`;
}

export function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

// Deterministic, templated cover-page description — no Groq call needed,
// which also sidesteps any risk of generic AI phrasing for this section.
// Matches the spec's own example almost exactly.
export function buildScopeDescription(metrics) {

  const { rowCount } = metrics.meta;
  const { measures, dimensions } = metrics.meta.analysisScope;

  const period = metrics.timePeriod
    ? ` covering the period from ${metrics.timePeriod.start} to ${metrics.timePeriod.end}`
    : "";

  const evaluated = [];

  if (measures.length) {
    evaluated.push(`total and average ${measures.map(humanize).join(" and ")}`);
  }

  if (metrics.timePeriod) {
    evaluated.push("year-over-year performance");
  }

  if (metrics.repPerformance) {
    evaluated.push(`${humanize(metrics.repPerformance.dimension).toLowerCase()} contribution`);
  }

  if (dimensions.length && !metrics.repPerformance) {
    evaluated.push(`${dimensions.slice(0, 2).map(humanize).join(" and ")} performance`);
  }

  const evaluatedText = evaluated.length
    ? ` The analysis evaluates ${joinList(evaluated)}.`
    : "";

  return `This report analyzes ${rowCount.toLocaleString()} records${period}.${evaluatedText}`;
}

function joinList(items) {
  if (items.length <= 1) return items.join("");
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
}
